import React, { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Typography,
  Box,
  Paper,
  Collapse,
  IconButton,
  TextField,
  Button,
  Chip,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import {
  fetchGciTrackerSettings,
  updateGciTrackerSettings,
} from "../../store/modules/gciTracker/gciTrackerActions";
import { getBusinessPlanningData } from "../../store/modules/analytics/analyticsActions";
import { clearBusinessPlanningData } from "../../store/modules/analytics/analyticsStateActions";
import { recalculateFinancialSettings } from "../../store/modules/common/commonActions";
import { BusinessPlanningTrendChart } from "../Charts/BusinessPlanningTrendChart";
import {
  calculateProjectedEffectiveGci,
  calculateProjectedGci,
  generateProjectedGciTrend,
} from "../../utils/BusinessPlanningCalculator";
import { formatCurrency } from "../../utils/common";

export const GciTrackerContainer: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();

  const { settings, loading } = useSelector(
    (state: RootState) => state.gciTracker,
  );
  const businessPlanning = useSelector(
    (state: RootState) => state.analytics.businessPlanning,
  );
  const transactionYears = useSelector(
    (state: RootState) => state.transactionYear.years,
  );
  const { brokerageAnniversaries } = useSelector(
    (state: RootState) => state.financialSettings,
  );
  const { status, hasHandledImpersonation, hasHandledStopImpersonation } =
    useSelector((state: RootState) => state.admin);
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );

  const [isExpanded, setIsExpanded] = useState(true);
  const [targetIncome, setTargetIncome] = useState<string>("150000");
  const [selectedYearIds, setSelectedYearIds] = useState<number[]>([]);
  const [recalculateDialogOpen, setRecalculateDialogOpen] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);

  const sortedYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [...transactionYears]
      .filter((y) => y.year !== currentYear)
      .sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
  }, [transactionYears]);

  // Get the brokerage anniversary for the current year
  const getCurrentYearBrokerageAnniversary = useMemo(() => {
    if (
      !brokerageAnniversaries.data ||
      brokerageAnniversaries.data.length === 0
    ) {
      return null;
    }

    const currentYear = new Date().getFullYear();

    // Helper to extract year from anniversary date
    const getYearFromDate = (date: any): number | null => {
      if (!date) return null;
      if (typeof date.year === "function") return date.year();
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) return parsed.getFullYear();
      return null;
    };

    // Sort by anniversary date descending
    const sortedAnniversaries = [...brokerageAnniversaries.data].sort(
      (a, b) => {
        const dateA = getYearFromDate(a.anniversaryDate) || 0;
        const dateB = getYearFromDate(b.anniversaryDate) || 0;
        return dateB - dateA;
      },
    );

    return (
      sortedAnniversaries.find((ann) => {
        const annYear = getYearFromDate(ann.anniversaryDate);
        return annYear !== null && annYear <= currentYear;
      }) ||
      sortedAnniversaries[sortedAnniversaries.length - 1] ||
      null
    );
  }, [brokerageAnniversaries.data]);

  // Fetch GCI tracker settings on mount
  useQuery({
    queryKey: ["gciTrackerSettings"],
    queryFn: () => {
      dispatch(fetchGciTrackerSettings());
      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
  });

  // Update local state when settings are fetched
  useEffect(() => {
    if (settings) {
      setTargetIncome(settings.targetAnnualIncome?.toString() || "");

      // Only set defaults if user has no stored years in DB
      if (settings.evaluationYears?.length === 0 && sortedYears.length > 0) {
        // Set default evaluation years (first 3 years from sorted years, excluding current)
        const defaultYearIds = sortedYears
          .slice(0, 3)
          .map((y) => y.id)
          .filter((id): id is number => id !== undefined);

        if (defaultYearIds.length > 0) {
          setSelectedYearIds(defaultYearIds);

          // Save defaults to database
          const defaultYears = defaultYearIds
            .map((id) => sortedYears.find((y) => y.id === id)?.year)
            .filter((year): year is number => year !== undefined);

          dispatch(
            updateGciTrackerSettings({
              targetAnnualIncome: settings.targetAnnualIncome,
              evaluationYears: defaultYears,
            }),
          );
        }
      } else if (
        settings.evaluationYears &&
        settings.evaluationYears.length > 0
      ) {
        const currentYear = new Date().getFullYear();
        // Convert stored evaluation years to year IDs, excluding current year
        const yearIds = settings.evaluationYears
          .filter((year) => year !== currentYear)
          .map((year) => transactionYears.find((y) => y.year === year)?.id)
          .filter((id): id is number => id !== undefined);

        // Only update if different from current selection to avoid duplicate fetch
        const currentSet = new Set(selectedYearIds);
        const newSet = new Set(yearIds);
        const isDifferent =
          currentSet.size !== newSet.size ||
          [...currentSet].some((id) => !newSet.has(id));

        if (isDifferent) {
          setSelectedYearIds(yearIds);
        }
      }
    }
  }, [settings, sortedYears, transactionYears, dispatch]);

  // Fetch business planning data when years or co-agent filter change
  useEffect(() => {
    if (selectedYearIds.length > 0) {
      dispatch(
        getBusinessPlanningData(
          selectedYearIds,
          "thisYear",
          ["type"],
          false,
        ) as any,
      );
    } else {
      // Clear stale data when no years are selected
      dispatch(clearBusinessPlanningData());
    }
  }, [selectedYearIds, selectedCoAgentIds, dispatch]);

  // Refresh data when impersonating or stopping impersonation
  useEffect(() => {
    if (status === "Impersonated" && !hasHandledImpersonation) {
      // Fetch fresh GCI tracker settings for the impersonated user
      dispatch(fetchGciTrackerSettings());
    } else if (status === "Stopped" && !hasHandledStopImpersonation) {
      // Fetch fresh GCI tracker settings for the original user
      dispatch(fetchGciTrackerSettings());
    }
  }, [status, hasHandledImpersonation, hasHandledStopImpersonation, dispatch]);

  const handleTargetIncomeBlur = () => {
    const numericValue = parseFloat(targetIncome) || 0;
    if (settings && numericValue !== settings.targetAnnualIncome) {
      const selectedYears = selectedYearIds
        .map((id) => transactionYears.find((y) => y.id === id)?.year)
        .filter((year): year is number => year !== undefined);

      dispatch(
        updateGciTrackerSettings({
          targetAnnualIncome: numericValue,
          evaluationYears: selectedYears,
        }),
      );
    }
  };

  const handleYearsChange = (event: any) => {
    const newYearIds = event.target.value as number[];
    setSelectedYearIds(newYearIds);

    // Don't send PUT request if all years are unchecked
    if (newYearIds.length === 0) {
      return;
    }

    if (settings) {
      const selectedYears = newYearIds
        .map((id) => transactionYears.find((y) => y.id === id)?.year)
        .filter((year): year is number => year !== undefined);

      dispatch(
        updateGciTrackerSettings({
          targetAnnualIncome: parseFloat(targetIncome) || 0,
          evaluationYears: selectedYears,
        }),
      );
    }
  };

  const handleToggleExpand = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
  };

  const handleRecalculateClick = () => {
    setRecalculateDialogOpen(true);
  };

  const handleRecalculateConfirm = async () => {
    setIsRecalculating(true);
    try {
      await recalculateFinancialSettings();
      setRecalculateDialogOpen(false);

      // Refresh GCI tracker data after recalculation
      if (selectedYearIds.length > 0) {
        dispatch(
          getBusinessPlanningData(
            selectedYearIds,
            "thisYear",
            ["type"],
            false,
          ) as any,
        );
      }
    } catch (error) {
      console.error("Error recalculating financials:", error);
    } finally {
      setIsRecalculating(false);
    }
  };

  // Calculate projected GCI
  const projectedGci = React.useMemo(() => {
    if (!businessPlanning?.data || !targetIncome) return null;

    const numericTargetIncome = parseFloat(targetIncome) || 0;

    const typeCategory = businessPlanning.data.categories?.find(
      (c) => c.category === "type",
    );

    // Filter to only include S&B transactions using the isSb flag
    const sbItems =
      typeCategory?.items?.filter((item) => item.isSb === true) || [];

    const totalGci = sbItems.reduce(
      (sum, item) => sum + (item.totalGci || 0),
      0,
    );
    const totalEffectiveGci = sbItems.reduce(
      (sum, item) => sum + (item.totalEffectiveGci || 0),
      0,
    );

    if (totalGci === 0 || totalEffectiveGci === 0) return null;

    // First calculate projected effective GCI based on target income and brokerage split
    const projectedEffectiveGci = calculateProjectedEffectiveGci(
      numericTargetIncome,
      getCurrentYearBrokerageAnniversary,
    );

    // Then calculate projected GCI based on the ratio from historical data (S&B only)
    return calculateProjectedGci(
      projectedEffectiveGci,
      totalGci,
      totalEffectiveGci,
    );
  }, [businessPlanning, targetIncome, getCurrentYearBrokerageAnniversary]);

  // Generate projected trend
  const projectedTrend = React.useMemo(() => {
    if (!businessPlanning?.data || !projectedGci) return [];

    const monthlyTrend = businessPlanning.data.monthlyTrend || [];
    const totalGci =
      businessPlanning.data.categories?.find((c) => c.category === "type")
        ?.totalGci || 0;

    return generateProjectedGciTrend(monthlyTrend, totalGci, projectedGci);
  }, [businessPlanning, projectedGci]);

  if (loading && !settings) {
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={2} sx={{ mb: 3 }}>
        {/* Header */}
        <Box
          onClick={handleToggleExpand}
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton size="small" sx={{ mr: 1 }}>
              {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
            <Typography
              variant="h6"
              component="h3"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              GCI Tracker
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              opacity: 0.7,
              transition: "opacity 0.2s ease-in-out",
              display: { xs: "none", sm: "block" },
            }}
          >
            Click to {isExpanded ? "collapse" : "expand"}
          </Typography>
        </Box>

        {/* Content */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: 3,
              }}
            >
              {/* Left Side - Controls */}
              <Box
                sx={{
                  width: isMobile ? "100%" : "240px",
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  pr: isMobile ? 0 : 3,
                  borderRight: isMobile ? "none" : "1px solid",
                  borderColor: "divider",
                }}
              >
                {/* Target Annual Income */}
                <NumericFormat
                  fullWidth
                  valueIsNumericString
                  customInput={TextField}
                  thousandSeparator
                  onValueChange={(values) => setTargetIncome(values.value)}
                  onBlur={handleTargetIncomeBlur}
                  allowLeadingZeros={false}
                  decimalScale={0}
                  value={targetIncome}
                  label="Target Annual Income"
                  allowNegative={false}
                  placeholder="Enter your target income"
                  size="small"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  inputProps={{
                    maxLength: 15,
                  }}
                />

                {/* Evaluation Years */}
                <FormControl fullWidth size="small">
                  <InputLabel shrink>Evaluation Years</InputLabel>
                  <Select
                    multiple
                    value={selectedYearIds}
                    displayEmpty
                    notched
                    onChange={handleYearsChange}
                    label="Evaluation Years"
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <em>Select years</em>;
                      }
                      return (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((id) => (
                            <Chip
                              key={id}
                              label={
                                transactionYears.find((y) => y.id === id)?.year
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      PaperProps: { style: { maxHeight: 300 } },
                    }}
                  >
                    {sortedYears.map((year) => (
                      <MenuItem key={year.id} value={year.id}>
                        <Checkbox
                          checked={
                            selectedYearIds.indexOf(year.id as number) > -1
                          }
                        />
                        <ListItemText primary={year.year} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Projected GCI Display */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    p: 1.5,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    Projected GCI
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "primary.main",
                      fontWeight: 700,
                      fontSize: "1.25rem",
                    }}
                  >
                    {projectedGci ? formatCurrency(projectedGci) : "—"}
                  </Typography>
                  {projectedGci && (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.65rem", lineHeight: 1.3, mt: 0.5 }}
                      >
                        To achieve{" "}
                        <strong>
                          {formatCurrency(parseFloat(targetIncome) || 0)}
                        </strong>{" "}
                        net commission income.
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.6rem", lineHeight: 1.3 }}
                      >
                        Based on{" "}
                        {getCurrentYearBrokerageAnniversary?.brokerageSplit
                          ? `${getCurrentYearBrokerageAnniversary.brokerageSplit}% brokerage split${
                              getCurrentYearBrokerageAnniversary.splitCap
                                ? ` (capped at ${formatCurrency(
                                    getCurrentYearBrokerageAnniversary.splitCap,
                                  )})`
                                : ""
                            }`
                          : "no brokerage split"}
                        {getCurrentYearBrokerageAnniversary?.additionalChargePercentage
                          ? `, additional ${getCurrentYearBrokerageAnniversary.additionalChargePercentage}% brokerage charge${
                              getCurrentYearBrokerageAnniversary.additionalChargeCap
                                ? ` (capped at ${formatCurrency(
                                    getCurrentYearBrokerageAnniversary.additionalChargeCap,
                                  )})`
                                : ""
                            }`
                          : ""}
                        , and referral fees, if any.
                      </Typography>
                    </>
                  )}
                </Box>

                <Button
                  variant="outlined"
                  onClick={handleRecalculateClick}
                  disabled={isRecalculating}
                  size="small"
                  fullWidth
                >
                  {isRecalculating
                    ? "Recalculating..."
                    : "Recalculate All Financials"}
                </Button>
              </Box>

              {/* Right Side - Chart */}
              <Box sx={{ flexGrow: 1, minWidth: 0, minHeight: "300px" }}>
                {selectedYearIds.length > 0 && projectedTrend.length > 0 ? (
                  <BusinessPlanningTrendChart
                    projectedTrend={projectedTrend}
                    currentYearTrend={
                      businessPlanning?.data?.currentYearMonthlyTrend
                    }
                    underContractGci={businessPlanning?.data?.underContractGci}
                    underContractLastMonth={
                      businessPlanning?.data?.underContractLastMonth
                    }
                    currentGciWithUnderContract={
                      businessPlanning?.data?.currentGciWithUnderContract
                    }
                    forecastYear={new Date().getFullYear()}
                    compact={true}
                  />
                ) : (
                  <Alert severity="info">
                    Select evaluation years to view GCI trend projection
                  </Alert>
                )}
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Recalculate Confirmation Dialog */}
      <Dialog
        open={recalculateDialogOpen}
        onClose={() => !isRecalculating && setRecalculateDialogOpen(false)}
      >
        <DialogTitle>Recalculate All Financials?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will recalculate all financial settings for your transactions.
            This process may take a few moments. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRecalculateDialogOpen(false)}
            disabled={isRecalculating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRecalculateConfirm}
            variant="contained"
            disabled={isRecalculating}
          >
            {isRecalculating ? "Recalculating..." : "Recalculate"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
